import ButtonBlock from "@components/ButtonBlock";
import ContentImage from "@components/ContentImage";
import Embed from "@components/Embed";
import FileDownloadButtons from "@components/FileDownloadButtons";
import FormSection from "@components/FormSection";
import HeadingTag from "@components/HeadingTag";
import Spacer from "@components/Spacer";
import VideoEmbed from "@components/VideoEmbed";
import StatusBarDots from "@images/StatusBar-Dots.svg";
import clsx from "clsx";
import parse from "html-react-parser";
import Image from 'next/image';
import React, { ReactNode } from "react";
import { useInView } from "react-intersection-observer";

const Component = ({ section }: { section: any }) => {
  let image;
  let imageBlur;

  switch (section.type) {
    case "heading":
      if (!section.heading) return null;
      return (
        <div className={clsx("ContentSections__heading", "max-w-3xl")}>
          <HeadingTag
            tag={section.headingTag}
            className={clsx({
              "text-3xl md:text-4xl 3xl:text-5xl":
                !section.headingSize || section.headingSize === "xl",
              "text-2xl md:text-3xl": section.headingSize === "lg",
              "text-lg md:text-2xl": section.headingSize === "md",
              "text-md md:text-xl": section.headingSize === "sm",
            })}
          >
            {section.heading}
          </HeadingTag>
        </div>
      );

    case "image":
      image = section.image[0];
      imageBlur = section.imageBlur[0];
      if (!image) return null;
      return (
        <div className="Attack__Screenshot-container bg-white-03 p-15 space-y-15">
          <div className="Attack__Screenshot border border-gray-03">
            <div className="Attack__Screenshot-statusBar pl-2 pb-1.5 bg-gray-03">
              <Image
                src={StatusBarDots}
                alt="Status Bar Dots"
                layout="fixed"
                width={27}
                height={7}
              />
            </div>
            <div className="Attack__Screenshot-image p-7.5 bg-white-01">
              <ContentImage
                image={image}
                imageBlur={imageBlur}
                caption={section.caption}
                captionPosition={section.captionPosition}
              />
            </div>
          </div>
        </div>
      );

    case "embed":
      return <Embed embedCode={section.embedCode} />;

    case "spacer":
      return <Spacer size={section.size} />;

    case "form":
      const form = !!section.form ? section.form[0] : null;

      return (
        <FormSection
          form={form}
          formHeading={section.formHeading}
          formDescription={!!section?.formDescription && parse(section.formDescription)}
          legacyBackgroundColor="#fed141"
        />
      );

    case "button":
      return (
        <ButtonBlock
          target={section.target}
          buttonBackgroundColor={section.buttonBackgroundColor}
          buttonTextColor={section.buttonTextColor}
          horizontalAlignment={section.horizontalAlignment}
        />
      );

    default:
      return !!section?.html && (
        <div className="prose max-w-none">
          {parse(section.html && section.html)}
        </div>
      );
  }
};

interface ISectionProps {
  children: ReactNode;
  typeHandle: string;
}

const Section = ({ children, typeHandle }: ISectionProps): JSX.Element => {
  const { ref, inView } = useInView();

  const className = clsx(
    "ContentSection",
    `ContentSection--${typeHandle}`,
    "transition-opacity duration-500 ease-out",
    {
      "opacity-0": !inView,
      "opacity-100": inView,
    }
  );

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

interface IProps {
  sections: any[];
}

const ContentSections = ({ sections }: IProps): JSX.Element => {
  return (
    <div className={clsx("ContentSections", "space-y-7.5 lg:space-y-10")}>
      {sections.map((section, index) => {
        return (
          <Section typeHandle={section.type} key={section.id}>
            <Component section={section} />
          </Section>
        );
      })}
    </div>
  );
};

export default ContentSections;
